import React, { FC, useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import {
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography
} from '@mui/material';
import {
  ExpandMore,
  AssignmentInd,
  Restore,
  Delete,
  Notes,
  ArrowDropDownCircleOutlined,
  RestartAlt,
  RestorePage,
  Visibility
} from '@mui/icons-material';
import { IBuild, IFieldWalkthrough, IBuildStatus } from '../../../models';
import { getBuildFieldWalkthroughs, resetFieldWalkthrough, postAddendum } from '../../../fetch';
import { useHistory } from 'react-router-dom';
import { ActionButtons } from '../../../components';

interface IMobileManageBuild {
  original: IBuild;
  handleConvertToSpec: (val: IBuild) => void;
  handleReset: (val: IBuild) => void;
  handleDelete: (val: IBuild) => void;
  handleTestContract: (val: IBuild) => void;
  regenerateTenDayAcceptance: (val: IBuild) => void;
  handleResetFieldWalkthrough: (val: IBuild, fieldwalkthroughs: IFieldWalkthrough[]) => void;
  statuses: IBuildStatus[];
  fetchBuilds: () => void;
  canRegenerateTenDayAcceptance: boolean;
}

export const MobileManageBuild: FC<IMobileManageBuild> = ({
  original,
  handleTestContract,
  handleConvertToSpec,
  handleReset,
  handleDelete,
  handleResetFieldWalkthrough,
  statuses,
  fetchBuilds,
  regenerateTenDayAcceptance
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const classes = useStyles();
  const [isResetting, setResetting] = useState(false);
  const [isGenerating, setGenerating] = useState(false);

  const { neighborhood, lotNumber, phase, planDescription, alert, status, buyer, agents } =
    original;

  const selection = statuses?.find(s => s.value === original.status);
  const testContractDisabled =
    selection?.description === 'New' || selection?.description === '' ? true : false;

  const canRegenerateTenDayAcceptance = useMemo(
    () =>
      original.status === 'TenDayComplete' ||
      original.status === 'Closing' ||
      original.status === 'Complete',
    [original]
  );

  // array for icon action buttons
  const buttonConfigs = [
    {
      icon: original.isSpec && original.specId ? <Visibility /> : <ArrowDropDownCircleOutlined />,
      color: 'primary' as 'primary',
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        original.specId && original.isSpec
          ? history.push(`/development/specs/${original.specId}`)
          : handleConvertToSpec(original);
      },
      className: classes.editButton
    },
    {
      icon: <AssignmentInd />,
      color: 'primary' as 'primary',
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        handleTestContract(original);
      },
      disabled: testContractDisabled,
      className: classes.editButton
    },
    {
      icon: <Notes />,
      color: 'primary' as 'primary',
      onClick: async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setGenerating(true);
        try {
          await postAddendum(original.buildId);
          enqueueSnackbar(`Addendum F Generated!`, {
            variant: 'success'
          });
          fetchBuilds();
        } catch (error) {
          enqueueSnackbar(error?.Detail ?? 'Error generating Test Addendum F, please try again.', {
            variant: 'error'
          });
        } finally {
          setGenerating(false);
        }
      },
      disabled: isGenerating,
      className: classes.editButton
    },
    {
      icon: <RestorePage />,
      color: 'primary' as 'primary',
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        regenerateTenDayAcceptance(original);
      },
      disabled: !canRegenerateTenDayAcceptance,
      className: classes.editButton
    },
    {
      icon: <Restore />,
      color: 'primary' as 'primary',
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        handleReset(original);
      },
      className: classes.editButton
    },
    {
      icon: <RestartAlt />,
      color: 'secondary' as 'secondary',
      onClick: async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        const fieldWalkthroughs = await getBuildFieldWalkthroughs(original.buildId, {
          isComplete: true
        });
        if (fieldWalkthroughs?.length === 0) {
          return window.alert('No field walkthroughs to reset.');
        }
        if (fieldWalkthroughs?.length === 1) {
          setResetting(true);
          try {
            await resetFieldWalkthrough(fieldWalkthroughs?.[0]?.fieldWalkthroughId);
            enqueueSnackbar(`Field Walkthrough reset!`, {
              variant: 'success'
            });
            fetchBuilds();
          } catch (error) {
            enqueueSnackbar(
              error?.Detail ?? 'Error resetting field walkthrough, please try again.',
              {
                variant: 'error'
              }
            );
          } finally {
            setResetting(false);
          }
          return;
        }
        return handleResetFieldWalkthrough(original, fieldWalkthroughs);
      },
      disabled: isResetting,
      className: classes.editButton
    },
    {
      icon: <Delete />,
      color: 'default' as 'default',
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        handleDelete(original);
      },
      className: classes.deleteButton
    }
  ];

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">
            {`${neighborhood} Phase: ${phase} Lot: ${lotNumber}`}
          </Typography>
        </div>
      </AccordionSummary>
      <ActionButtons configs={buttonConfigs} classes={classes} />
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Neighborhood:
          </FormLabel>
          <Typography component="span" variant="body2">
            {neighborhood || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Lot:
          </FormLabel>
          <Typography component="span" variant="body2">
            {lotNumber || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Phase:
          </FormLabel>
          <Typography component="span" variant="body2">
            {phase || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Plan:
          </FormLabel>
          <Typography component="span" variant="body2">
            {planDescription || '--'}
          </Typography>
        </div>
        {alert && (
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Alert Status:
            </FormLabel>
            <Typography component="span" variant="body2">
              {alert || '--'}
            </Typography>
          </div>
        )}
        {agents && agents.length > 0 && (
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Agents:
            </FormLabel>
            <Typography component="span" variant="body2">
              {agents.map((agent, index) => {
                return (
                  <span key={`${index}`}>
                    {agent.firstName} {agent.lastName}
                    {index !== agents.length - 1 ? ', ' : ''}
                  </span>
                );
              })}
            </Typography>
          </div>
        )}
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Buyer:
          </FormLabel>
          <Typography component="span" variant="body2">
            {buyer || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Status:
          </FormLabel>
          <Typography component="span" variant="body2">
            {status || '--'}
          </Typography>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 },
    paddingTop: '.25rem'
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  roleWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    padding: `2px 5px`
  },
  deleteButton: {
    padding: `2px 5px`,
    color: theme.palette.error.main
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '.75rem .25rem .75rem .25rem',
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    borderBottom: `1px solid ${theme.palette.grey[300]}`
  }
}));
